import { Grid, Box } from "@mui/material";
import ComplianceNavigatorCards from "./ComplianceNavigatorCards";
import { useEffect, useState } from "react";
import ComplianceSummary from "../../../Components/ComplianceNavigator/ComplianceSummary";
import ComplianceNavigatorHeader from "../../../Components/ComplianceNavigator/ComplianceNavigatorHeader";
import { changeMode, changeModeForField, prepareDefaultValues, prepareInitialConfig } from "../../../../utils/formHelper";
import { FormProvider, useForm } from "react-hook-form";
import { getApi } from "../../../../utils/services";
import { COMPLIANCE_DOCUMENTS, GET_ALL_PARTNERS_DROPDOWN, GET_ALL_PROJECT } from "../../../../utils/services/apiPath";
import Loading from "../../../Components/Loading";
import AppendixATable from "../../../Components/ComplianceNavigator/AppendixATable";
import AppendixDTable from "../../../Components/ComplianceNavigator/AppendixDTable";
import SixSevenOneTable from "../../../Components/ComplianceNavigator/SixSevenOneTable";
import PrevailingWageSummaryTable from "../../../Components/ComplianceNavigator/PrevailingWageSummaryTable";
import CPRTable from "../../../Components/ComplianceNavigator/CPRTable";
import CLRFiledTable from "../../../Components/ComplianceNavigator/CLRFiledTable";
import HistoryOfUnderpaymentsAndProofOfCorrectionsAndFinesTable from "../../../Components/ComplianceNavigator/HistoryOfUnderpaymentsAndProofOfCorrectionsAndFinesTable";
import NintyDayCertificates from "../../../Components/ComplianceNavigator/NintyDayCertificates";
import RatioManagement from "../../../Components/ComplianceNavigator/RatioManagement";
import { useFeatureFlagEnabled } from "posthog-js/react";
import JourneyWorkerCertification from "../../../Components/ComplianceNavigator/JourneyWorkerCertification";
import { useSelector } from "react-redux";
import { subModPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { R_ALL_DOCUMENTS, R_DOCUMENTS_671_FORM } from "../../../../utils/permissionReferenceConfig";

const complianceConfig = {
  projectId: {
    label: "Project",
    perKey: "Project",
    placeholder: "All Project"
  },
  partnerId: {
    label: "Partner",
    perKey: "Partner",
    placeholder: "All Partners"
  }
};

const ComplianceNavigator = () => {
  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]);
  const isAddMode = true;
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(complianceConfig));
  const methods = useForm({ defaultValues: prepareDefaultValues(complianceConfig) });
  const { control, setValue, watch } = methods;
  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));
  const onEdit = () => setFormConfig(changeMode("edit"));
  const [projectsList, setProjectsList] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const project = watch("projectId");
  const partner = watch("partnerId");
  const experimentalNavigatorTilesEnabled = useFeatureFlagEnabled("experimental-compliance-navigator-tiles-enabled");
  const includeAppendixD = useFeatureFlagEnabled("show-appendix-d-PSPM-701");
  const include671 = useFeatureFlagEnabled("show-671-PSPM-701");
  const { canExport: canExport671 } = useSelector(subModPermissionSelector(R_ALL_DOCUMENTS, R_DOCUMENTS_671_FORM));


  const typeOptions = {
    projectId: projectsList,
    partnerId: partnersList
  };

  const fetchProjects = async () => {
    const { data } = await getApi(GET_ALL_PROJECT);
    setValue("projectId", data[0]?.projectId);
    setProjectsList(data?.map(item => ({ label: item.projectName, value: item.projectId })));
  };

  const fetchPartners = async () => {
    // V2 API expects empty or null to send back "All" Partners
    let pId = project;
    if (pId === "All") {
      pId = "";
    }
    const res = await getApi(`${GET_ALL_PARTNERS_DROPDOWN}?projectId=${pId}`);

    const partners = res.data;
    const partnersList = partners.map(item => ({ label: item.companyName, value: item.clientGuid }));

    // prepend the "All" value as V2 API does not send this back in response.
    partnersList.unshift({ label: "All Partners", value: "All" });

    setValue("partnerId", "All");
    setPartnersList(partnersList);
  };

  const fetchAllComplianceDocument = async () => {
    setLoading(true);
    const { complianceDocument, summary } = await getApi(`${COMPLIANCE_DOCUMENTS}?projectId=${project}&partnerId=${partner}`);
    const filteredCards = complianceDocument.filter(
      item =>
        (item.key === "671" && include671) ||
        (item.key === "AppendixD" && includeAppendixD && item.total > 0) ||
        (item.key !== "671" && item.key !== "AppendixD")
    );
    const filteredSummaryList = summary?.summaryList?.filter(
      item => (include671 || !item.includes("671 Form")) && (includeAppendixD || !item.includes("Appendix D"))
    );

    const formatted = getTableComponent(filteredCards);
    if (experimentalNavigatorTilesEnabled) {
      formatted.push({
        title: "Mentor/Apprentice Log",
        available: 240,
        total: 240,
        chipText: "Available: 240/240",
        tableComponent: () => <div>Under Construction</div>
      });
      formatted.push({
        title: "Apprentice WPS Log",
        available: 420,
        total: 420,
        chipText: "Available: 420/420",
        tableComponent: () => <div>Under Construction</div>
      });
      formatted.push({
        title: "CLR",
        available: 420,
        total: 420,
        chipText: "Available: 420/420",
        tableComponent: () => <CLRFiledTable tableKey="CLRFiled" projectId={project} partnerId={partner} />
      });
      formatted.push({
        title: "Ratio Management",
        available: 420,
        total: 420,
        chipText: "Available: 420/420",
        tableComponent: () => <RatioManagement tableKey="RatioManagement" projectId={project} partnerId={partner} />
      });
      formatted.push({
        title: "History Of Underpayment",
        available: 420,
        total: 420,
        chipText: "Available: 420/420",
        tableComponent: () => (
          <HistoryOfUnderpaymentsAndProofOfCorrectionsAndFinesTable tableKey="HistoryOfUnderpayment" projectId={project} partnerId={partner} />
        )
      });
    }
    const completedCards = formatted.filter(card => card.available >= card.total);

    const completedCount = completedCards.length;
    const totalCount = formatted.length;

    const completedSummaryList = completedCards.map(card => `${card.title} Completed`);

    const updatedSummary = {
      completedCount,
      total: totalCount,
      summaryList: completedSummaryList,
      apiSummaryList: filteredSummaryList || [],
      combinedSummaryList: [...completedSummaryList, ...(filteredSummaryList || [])]
    };

    setLoading(false);
    setCards(formatted);
    setSummary(updatedSummary);
  };

  const getTableComponent = complianceDocument => {
    const tableComponents = {
      AppendixA: <AppendixATable tableKey="AppendixA" projectId={project} partnerId={partner} />,
      AppendixD: <AppendixDTable tableKey="AppendixD" projectId={project} partnerId={partner} />,
      671: <SixSevenOneTable tableKey="671" projectId={project} partnerId={partner} />,
      PrevailingWageSummary: <PrevailingWageSummaryTable tableKey="PrevailingWageSummary" projectId={project} partnerId={partner} />,
      DavisBaconForm: <NintyDayCertificates tableKey="DavisBaconForm" projectId={project} partnerId={partner} />,
      CPR: <CPRTable tableKey="CPR" projectId={project} partnerId={partner} />,
      JourneyWorkerCertificationForm: <JourneyWorkerCertification tableKey="JourneyWorkerCertificationForm" projectId={project} partnerId={partner} />
    };
    if (experimentalNavigatorTilesEnabled) {
      tableComponents["CLRFiled"] = <CLRFiledTable tableKey="CLRFiled" projectId={project} partnerId={partner} />;
      tableComponents["RatioManagement"] = <RatioManagement tableKey="RatioManagement" projectId={project} partnerId={partner} />;
      tableComponents["HistoryOfUnderpayment"] = (
        <HistoryOfUnderpaymentsAndProofOfCorrectionsAndFinesTable tableKey="HistoryOfUnderpayment" projectId={project} partnerId={partner} />
      );
    }

    let tables = [];
    complianceDocument?.map(table => {
      let tempTable = {
        ...table,
        tableComponent: () => tableComponents[table?.key],
        chipText: `Available: ${table.available}/${table.total}`,
        hideCheckbox: table.key === "671" && !canExport671

      };
      tables?.push(tempTable);
    });
    return tables;
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (project) {
      fetchPartners();
    }
  }, [project]);

  useEffect(() => {
    if (project && partner) {
      fetchAllComplianceDocument();
    }
  }, [project, partner]);

  useEffect(() => {
    onEdit();
  }, []);

  const toggleSelectedCards = cardTitle => {
    setSelectedCards(prevSelectedCards => {
      if (prevSelectedCards.includes(cardTitle)) {
        return prevSelectedCards.filter(title => title !== cardTitle);
      } else {
        return [...prevSelectedCards, cardTitle];
      }
    });
  };

  const defaultFormProps = { control, formValues: {}, isAddMode, handleReadValueClick };

  return (
    <FormProvider {...methods}>
      <div style={{ margin: "25px auto", padding: "0 1rem", maxWidth: 1440 }}>
        <ComplianceNavigatorHeader formProps={defaultFormProps} formConfig={formConfig} typeOptions={typeOptions} selectedCards={selectedCards} />
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2} direction={"row"}>
            <Grid item xs={9}>
              {loading ? (
                <Loading />
              ) : (
                <ComplianceNavigatorCards
                  cards={cards}
                  toggleSelectedCards={toggleSelectedCards}
                  selectedCards={selectedCards}
                  fetchAllComplianceDocument={fetchAllComplianceDocument}
                />
              )}
            </Grid>
            <Grid item xs={3}>
              <ComplianceSummary summaryJson={summary} cardCount={cards.length} projectCount={projectsList.length} />
            </Grid>
          </Grid>
        </Box>
      </div>
    </FormProvider>
  );
};

export default ComplianceNavigator;
